import { z } from "zod";

/** Evidence retention choices, as the retention page offers them. */
export const retentionChoices = ["30-days", "90-days", "1-year", "forever"] as const;
export const retentionChoiceSchema = z.enum(retentionChoices);
export type RetentionChoice = z.infer<typeof retentionChoiceSchema>;

/**
 * The current retention for a project. Shortening it needs a second owner to confirm, so the
 * response says whether the pending change is still awaiting one.
 */
export const retentionSchema = z.object({
  projectId: z.uuid(),
  retention: retentionChoiceSchema,
  pendingRetention: retentionChoiceSchema.nullable(),
  pendingRequestedBy: z.uuid().nullable(),
});
export type Retention = z.infer<typeof retentionSchema>;

/**
 * `PUT /api/v1/projects/:id/retention`. Shortening needs a second owner; lengthening applies at
 * once. The confirmation is what the second owner supplies, so it travels with the request.
 */
export const updateRetentionSchema = z.object({
  retention: retentionChoiceSchema,
  confirm: z.boolean().default(false),
});
export type UpdateRetention = z.infer<typeof updateRetentionSchema>;

/** Export states, as the export action badges them. */
export const exportStates = ["building", "ready", "expired", "failed"] as const;
export const exportStateSchema = z.enum(exportStates);
export type ExportState = z.infer<typeof exportStateSchema>;

/**
 * One export build. The link is emailed, not returned here; a secret is never part of the zip,
 * which the acceptance test asserts rather than this shape.
 */
export const projectExportSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  state: exportStateSchema,
  requestedAt: z.string().datetime(),
  readyAt: z.string().datetime().nullable(),
  expiresAt: z.string().datetime().nullable(),
});
export type ProjectExport = z.infer<typeof projectExportSchema>;

export const projectExportListSchema = z.array(projectExportSchema);
export type ProjectExportList = z.infer<typeof projectExportListSchema>;

/** What clearing memory removes. Sources can be added again, so this is recoverable. */
export const clearMemorySchema = z.object({
  sourceId: z.uuid().nullable().optional(),
});
export type ClearMemory = z.infer<typeof clearMemorySchema>;

/** Deletion request states, as the delete page badges them. */
export const deletionStates = ["requested", "approved", "purged"] as const;
export const deletionStateSchema = z.enum(deletionStates);
export type DeletionState = z.infer<typeof deletionStateSchema>;

/** Pending two-owner project deletion, including the scheduled purge date. */
export const projectDeletionRequestSchema = z.object({
  projectId: z.uuid(),
  state: deletionStateSchema.nullable(),
  requestedBy: z.uuid().nullable(),
  approvedBy: z.uuid().nullable(),
  requestedAt: z.string().datetime().nullable(),
  purgeAt: z.string().datetime().nullable(),
});
export type ProjectDeletionRequest = z.infer<typeof projectDeletionRequestSchema>;

/**
 * `POST /api/v1/projects/:id/delete`. A second owner must approve, so the request names who is
 * confirming. Without a second owner the deletion does not proceed.
 */
export const requestDeletionSchema = z.object({
  confirm: z.boolean().default(false),
});
export type RequestDeletion = z.infer<typeof requestDeletionSchema>;

/** What archive costs the project, per this task: unused starter credits are forfeited. */
export const archiveForfeitures = {
  unusedStarterCreditsForfeited: true,
  restoreWindowDays: 30,
} as const;
